import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link2, Loader2, X } from 'lucide-react';
import { apiClient } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { MappingCatalogResponse } from '@/types/api';

export type LinkMode = 'price' | 'meter';

/** A selectable entry on either side of the linker. */
interface CatalogEntry {
  id: string;
  name: string;
  summary?: string;
}

export interface StagedPair {
  hubspotId: string;
  hubspotName?: string;
  stripeId: string;
  stripeName?: string;
  /** Price mode only: bill this product as a negative (credit) line item. */
  isNegative?: boolean;
}

interface LinkMappingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: LinkMode;
  /** HubSpot product IDs that already have a mapping (hidden from the unlinked list). */
  mappedHubspotIds: Set<string>;
  /** Stripe price/meter IDs that already have a mapping (hidden from the unlinked list). */
  mappedStripeIds: Set<string>;
  /** Called with the staged pairs when the user hits Validate & Save. */
  onSubmit: (pairs: StagedPair[]) => void;
  submitting: boolean;
}

function formatAmount(unitAmount?: number | null, currency?: string | null): string | undefined {
  if (unitAmount == null) return undefined;
  return `${(unitAmount / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${(currency || 'usd').toUpperCase()}`;
}

interface SidePanelProps {
  title: string;
  placeholder: string;
  entries: CatalogEntry[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (entry: CatalogEntry) => void;
  filter: string;
  onFilterChange: (value: string) => void;
  /** IDs that are already mapped or staged — the "Use ID" fallback must not
   *  offer these, or the same ID could be staged twice. */
  unavailableIds: Set<string>;
  /** Catalog hit its fetch cap — more items exist upstream. */
  truncated?: boolean;
}

/** One platform column: input bar on top, scrollable unlinked list below. */
const SidePanel: React.FC<SidePanelProps> = ({
  title,
  placeholder,
  entries,
  loading,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  unavailableIds,
  truncated,
}) => {
  const query = filter.trim().toLowerCase();
  const filtered = query
    ? entries.filter(
        (e) =>
          e.id.toLowerCase().includes(query) ||
          e.name.toLowerCase().includes(query)
      )
    : entries;

  const exactMatch = entries.some((e) => e.id.toLowerCase() === query);
  const typedId = filter.trim();
  const typedIdUnavailable = typedId.length > 0 && unavailableIds.has(typedId);

  return (
    <div className="flex flex-col min-h-0">
      <p className="text-sm font-semibold text-foreground mb-2">{title}</p>
      <Input
        placeholder={placeholder}
        value={filter}
        onChange={(e) => onFilterChange(e.target.value)}
        className="mb-2"
      />
      <div className="border rounded-md h-56 overflow-y-auto divide-y">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {filtered.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => onSelect(entry)}
                className={cn(
                  'w-full text-left px-3 py-2 border-l-4 border-l-transparent hover:bg-accent focus:outline-none focus:bg-accent',
                  selectedId === entry.id &&
                    'bg-primary/25 border-l-primary hover:bg-primary/25 focus:bg-primary/25'
                )}
              >
                <p className="text-sm text-foreground truncate">{entry.name}</p>
                <p className="text-xs font-mono text-muted-foreground truncate">
                  {entry.id}
                  {entry.summary ? ` · ${entry.summary}` : ''}
                </p>
              </button>
            ))}
            {query && !exactMatch && typedIdUnavailable && (
              <p className="px-3 py-2 text-xs text-muted-foreground italic">
                &ldquo;{typedId}&rdquo; is already mapped or linked below
              </p>
            )}
            {query && !exactMatch && !typedIdUnavailable && (
              <button
                type="button"
                onClick={() => onSelect({ id: typedId, name: typedId })}
                className={cn(
                  'w-full text-left px-3 py-2 border-l-4 border-l-transparent hover:bg-accent focus:outline-none focus:bg-accent',
                  selectedId === typedId &&
                    'bg-primary/25 border-l-primary hover:bg-primary/25 focus:bg-primary/25'
                )}
              >
                <p className="text-sm text-foreground">
                  Use ID &ldquo;{typedId}&rdquo;
                </p>
                <p className="text-xs text-muted-foreground">
                  Not in the catalog — will be validated on save
                </p>
              </button>
            )}
            {filtered.length === 0 && !query && (
              <p className="px-3 py-8 text-sm text-center text-muted-foreground">
                No unlinked items
              </p>
            )}
          </>
        )}
      </div>
      {truncated && (
        <p className="mt-1 text-xs text-muted-foreground">
          Large catalog — only the first items are listed. Type an exact ID to
          use one that isn&rsquo;t shown.
        </p>
      )}
    </div>
  );
};

/**
 * Interactive two-column linker: pick one HubSpot product and one Stripe
 * price/meter, stage them as a pair, then validate & save all staged pairs
 * through the existing dry-run flow.
 */
const LinkMappingModal: React.FC<LinkMappingModalProps> = ({
  open,
  onOpenChange,
  mode,
  mappedHubspotIds,
  mappedStripeIds,
  onSubmit,
  submitting,
}) => {
  const [hubspotFilter, setHubspotFilter] = useState('');
  const [stripeFilter, setStripeFilter] = useState('');
  const [selectedHubspot, setSelectedHubspot] = useState<CatalogEntry | null>(null);
  const [selectedStripe, setSelectedStripe] = useState<CatalogEntry | null>(null);
  const [staged, setStaged] = useState<StagedPair[]>([]);

  // The parent closes the dialog programmatically after a successful
  // validation, which bypasses resetAndClose — so clear state on each open.
  useEffect(() => {
    if (open) {
      setStaged([]);
      setSelectedHubspot(null);
      setSelectedStripe(null);
      setHubspotFilter('');
      setStripeFilter('');
    }
  }, [open]);

  const { data: catalog, isLoading, isError } = useQuery<MappingCatalogResponse>({
    queryKey: ['mappingCatalog'],
    queryFn: () => apiClient.getMappingCatalog(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const stagedHubspotIds = useMemo(() => new Set(staged.map((p) => p.hubspotId)), [staged]);
  const stagedStripeIds = useMemo(() => new Set(staged.map((p) => p.stripeId)), [staged]);

  const hubspotEntries: CatalogEntry[] = useMemo(
    () =>
      (catalog?.hubspot_products ?? [])
        .filter((p) => !mappedHubspotIds.has(p.id) && !stagedHubspotIds.has(p.id))
        .map((p) => ({
          id: p.id,
          name: p.name || '(unnamed product)',
          summary: [
            p.price != null ? p.price.toLocaleString() : undefined,
            p.recurring_billing_frequency ?? undefined,
          ]
            .filter(Boolean)
            .join(' / ') || undefined,
        })),
    [catalog, mappedHubspotIds, stagedHubspotIds]
  );

  const stripeEntries: CatalogEntry[] = useMemo(() => {
    if (mode === 'meter') {
      return (catalog?.stripe_meters ?? [])
        .filter((m) => !mappedStripeIds.has(m.id) && !stagedStripeIds.has(m.id))
        .map((m) => ({
          id: m.id,
          name: m.display_name || m.event_name || '(unnamed meter)',
          summary: m.status ?? undefined,
        }));
    }
    return (catalog?.stripe_prices ?? [])
      .filter((p) => !mappedStripeIds.has(p.id) && !stagedStripeIds.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.product_name || p.nickname || '(unnamed price)',
        summary: [
          formatAmount(p.unit_amount, p.currency),
          p.interval ? `per ${p.interval}` : undefined,
        ]
          .filter(Boolean)
          .join(' '),
      }));
  }, [catalog, mode, mappedStripeIds, stagedStripeIds]);

  // IDs that must not be staged (again) on each side: already mapped, or
  // already in the staged list. Guards both the "Use ID" fallback and
  // handleLink so a duplicate HubSpot ID can never reach the save endpoint,
  // where the last write would win.
  const hubspotUnavailableIds = useMemo(
    () => new Set([...mappedHubspotIds, ...stagedHubspotIds]),
    [mappedHubspotIds, stagedHubspotIds]
  );
  const stripeUnavailableIds = useMemo(
    () => new Set([...mappedStripeIds, ...stagedStripeIds]),
    [mappedStripeIds, stagedStripeIds]
  );

  const handleLink = () => {
    if (!selectedHubspot || !selectedStripe) return;
    if (
      hubspotUnavailableIds.has(selectedHubspot.id) ||
      stripeUnavailableIds.has(selectedStripe.id)
    ) {
      // Stale selection (e.g. picked via "Use ID" before it was staged).
      setSelectedHubspot(null);
      setSelectedStripe(null);
      return;
    }
    setStaged((prev) => [
      ...prev,
      {
        hubspotId: selectedHubspot.id,
        hubspotName: selectedHubspot.name,
        stripeId: selectedStripe.id,
        stripeName: selectedStripe.name,
      },
    ]);
    setSelectedHubspot(null);
    setSelectedStripe(null);
    setHubspotFilter('');
    setStripeFilter('');
  };

  const handleUnstage = (index: number) => {
    setStaged((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleNegative = (index: number, isNegative: boolean) => {
    setStaged((prev) =>
      prev.map((pair, i) => (i === index ? { ...pair, isNegative } : pair))
    );
  };

  const resetAndClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStaged([]);
      setSelectedHubspot(null);
      setSelectedStripe(null);
      setHubspotFilter('');
      setStripeFilter('');
    }
    onOpenChange(nextOpen);
  };

  const stripeNoun = mode === 'meter' ? 'meter' : 'price';

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'meter' ? 'Link Products to Meters' : 'Link Products to Prices'}
          </DialogTitle>
          <DialogDescription>
            Select a HubSpot product and a Stripe {stripeNoun}, then link them
            together. Linked pairs are validated before saving.
            {mode === 'price' &&
              ' Turn on Negative for a product that should appear as a credit (a negative line) on the invoice.'}
          </DialogDescription>
        </DialogHeader>

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load catalogs. You can still type IDs into the search
              bars and link them manually.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SidePanel
            title="HubSpot Products"
            placeholder="Search or enter a product ID..."
            entries={hubspotEntries}
            loading={isLoading}
            selectedId={selectedHubspot?.id ?? null}
            onSelect={(e) => setSelectedHubspot(e)}
            filter={hubspotFilter}
            onFilterChange={setHubspotFilter}
            unavailableIds={hubspotUnavailableIds}
            truncated={catalog?.hubspot_products_truncated}
          />
          <SidePanel
            title={mode === 'meter' ? 'Stripe Meters' : 'Stripe Prices'}
            placeholder={`Search or enter a ${stripeNoun} ID...`}
            entries={stripeEntries}
            loading={isLoading}
            selectedId={selectedStripe?.id ?? null}
            onSelect={(e) => setSelectedStripe(e)}
            filter={stripeFilter}
            onFilterChange={setStripeFilter}
            unavailableIds={stripeUnavailableIds}
            truncated={
              mode === 'meter'
                ? catalog?.stripe_meters_truncated
                : catalog?.stripe_prices_truncated
            }
          />
        </div>

        <div className="flex justify-center">
          <Button
            variant="secondary"
            onClick={handleLink}
            disabled={!selectedHubspot || !selectedStripe}
          >
            <Link2 className="h-4 w-4 mr-2" />
            Link Selected
          </Button>
        </div>

        <div>
          <p className="text-sm font-semibold text-foreground mb-2">
            Linked pairs ({staged.length})
          </p>
          <div className="border rounded-md max-h-48 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>HubSpot Product</TableHead>
                  <TableHead>Stripe {mode === 'meter' ? 'Meter' : 'Price'}</TableHead>
                  {mode === 'price' && <TableHead className="w-24">Negative</TableHead>}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staged.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={mode === 'price' ? 4 : 3}
                      className="text-center text-muted-foreground py-6"
                    >
                      Nothing linked yet. Select one item on each side and click
                      &ldquo;Link Selected&rdquo;.
                    </TableCell>
                  </TableRow>
                ) : (
                  staged.map((pair, i) => (
                    <TableRow key={`${pair.hubspotId}-${pair.stripeId}`}>
                      <TableCell>
                        <p className="text-sm truncate">{pair.hubspotName}</p>
                        <p className="text-xs font-mono text-muted-foreground">{pair.hubspotId}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm truncate">{pair.stripeName}</p>
                        <p className="text-xs font-mono text-muted-foreground">{pair.stripeId}</p>
                      </TableCell>
                      {mode === 'price' && (
                        <TableCell>
                          {/* A credit: the product bills as a negative line on
                              the invoice. One-time products only — Stripe has
                              no negative recurring prices. */}
                          <Switch
                            checked={pair.isNegative ?? false}
                            aria-label={`Bill ${pair.hubspotId} as a negative line item`}
                            onCheckedChange={(checked) => handleToggleNegative(i, checked)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleUnstage(i)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => resetAndClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(staged)}
            disabled={staged.length === 0 || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Validate &amp; Save {staged.length > 0 ? `(${staged.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkMappingModal;
